import { Checkbox, FormControlLabel } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import useNavigateAfterCreate from 'components/shared/CRUD/components/InnerPage/hooks/useNavigateAfterCreate';
import withInnerLayout from 'components/shared/CRUD/components/InnerPage/WithInnerLayout';
import CustomDataGrid from 'components/shared/DataGrid/DataGrid';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountDetailsDrawer } from './AccountDetailsDrawer';
import { EditAccountGroups } from './EditAccountGroups';
import {
  getBackOnlyValidAccounts,
  useGetAccounts
} from './hooks/useGetAccounts';
import { useGetAccountsColumns } from './hooks/useGetAccountsColumns';

import type { TableCRUDLayoutProps } from 'components/shared/CRUD/TableCRUDLayout';
import type { Dispatch, FC, SetStateAction } from 'react';
import type { Account } from '../../../types';

interface Props {
  isInnerLayoutOpen: boolean;
  setIsInnerLayoutOpen: Dispatch<SetStateAction<boolean>>;
}

const Accounts: FC<Props> = ({ isInnerLayoutOpen, setIsInnerLayoutOpen }) => {
  'use memo';

  const [isValid, setIsValid] = useState<boolean>(true);
  const [rowData, setRowData] = useState<Account[]>([]);
  const [openDetailDrawer, setOpenDetailDrawer] = useState<boolean>(false);
  const [selectedAccount, setSelectedAccount] = useState<Account>();

  const { t } = useTranslation('common');
  const { data: accounts, isFetching } = useGetAccounts();
  const columns = useGetAccountsColumns();

  const handleAddAccount: TableCRUDLayoutProps<Account>['onAddClick'] = () => {
    setSelectedAccount(undefined);
    setOpenDetailDrawer(true);
  };

  const handleCloseDrawer = () => setOpenDetailDrawer(false);
  const toggleInnerLayout = () => setIsInnerLayoutOpen(!isInnerLayoutOpen);
  const handleViewClick = (_id: number | string, row?: Account) => {
    setSelectedAccount(row);
    setIsInnerLayoutOpen(true);
  };

  useNavigateAfterCreate(accounts, handleViewClick);

  useEffect(() => {
    if (!isFetching) {
      if (isValid) setRowData(getBackOnlyValidAccounts(accounts ?? []));
      else setRowData(accounts ?? []);
    }
  }, [isValid, isFetching, accounts]);

  if (isInnerLayoutOpen && selectedAccount) {
    return (
      <EditAccountGroups
        selectedAccount={selectedAccount}
        toggleInnerLayout={toggleInnerLayout}
        accounts={accounts ?? []}
      />
    );
  }

  return (
    <>
      {isFetching ? (
        <Skeleton />
      ) : (
        <CustomDataGrid<Account>
          rows={rowData}
          columns={columns}
          getRowId={(row) => row.accountId}
          pagination
          pageSize={50}
          pageSizeOptions={[50, 100]}
          onViewClick={handleViewClick}
          onAddClick={handleAddAccount}
          headerContent={
            <FormControlLabel
              label={t('onlyValidAcc')}
              control={
                <Checkbox
                  checked={isValid}
                  onChange={() => setIsValid(!isValid)}
                />
              }
            />
          }
        />
      )}
      <AccountDetailsDrawer
        onClose={handleCloseDrawer}
        open={openDetailDrawer}
      />
    </>
  );
};

const AccountsWithLayout = withInnerLayout(Accounts);
export default AccountsWithLayout;
